import { zodResolver } from '@hookform/resolvers/zod';
import Save from '@mui/icons-material/Save';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { createFileRoute } from '@tanstack/react-router';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { FieldErrors, FormProvider, Path, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FloatingSaveButton } from '@/components/floating-save-button';

import AuthGuard from '@/components/auth-guard';
import { BasicIdentitySection } from '@/components/organization-form/basic-identity';
import { CustomFieldsSection } from '@/components/organization-form/custom-fields-section';
import { ImageManager } from '@/components/organization-form/image-manager';
import { MembershipSection } from '@/components/organization-form/membership-section';
import { Page } from '@/components/page';
import {
  OrganizationFormSchema,
  OrganizationFormValues,
  useOrganizationAccessLayer,
} from '@/features/organizations';
import {
  getChildOrganizationIds,
  getOrganizationMemberIds,
  getParentOrganizationIds,
  syncCharactersForOrganization,
  syncChildOrganizationsForOrganization,
  syncParentOrganizationsForOrganization,
} from '@/features/organizations/utils/membership-sync';
import {
  ORGANIZATION_HIERARCHY_FEATURE_KEY,
  stripOrganizationHierarchyFields,
} from '@/features/organizations/utils/organization-hierarchy-feature';
import { useImageProcessor } from '@/features/shared/hooks/use-image-processor';
import { useEntities } from '@/hooks/use-entities';
import { useResponsiveValue } from '@/hooks/use-responsive-value';
import { useToast } from '@/hooks/use-toast';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import { useSubscription } from '@/hooks/useSubscription';
import { Character as DomainCharacter, Organization as DomainOrganization } from '@/lib/types';
import { splitBySchemaShape } from '@/lib/utils/split-by-schema';
import { useNavigate } from '@tanstack/react-router';

export const Route = createFileRoute('/organizations/new')({
  component: NewOrganizationPage,
});

function NewOrganizationForm() {
  const { novel, getNewId, updateEntityItem, saveItem, deleteItem } = useNovelRepository();
  const novelId = novel?.id;
  const { toast } = useToast();
  const resolveResponsive = useResponsiveValue();
  const { t } = useTranslation();
  const { hasFeature } = useSubscription();
  const isOrganizationHierarchyEnabled = hasFeature(ORGANIZATION_HIERARCHY_FEATURE_KEY);

  const organizationId = useMemo(() => getNewId(), [getNewId]);

  const {
    createOrganization: createOrganizationLayer,
    organization: combinedData,
    sections: organizationSettings,
  } = useOrganizationAccessLayer(organizationId);


  const { processImages, isProcessing: isProcessingImages } = useImageProcessor();
  const { data: characterDocs, isLoading: isLoadingCharacters } = useEntities<DomainCharacter>('characters', isOrganizationHierarchyEnabled);
  const { data: organizationDocs, isLoading: isLoadingOrganizations } = useEntities<DomainOrganization>('organizations', isOrganizationHierarchyEnabled);
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const isProcessing = isProcessingImages;
  const isLoadingHierarchyRecords = isOrganizationHierarchyEnabled && (isLoadingCharacters || isLoadingOrganizations);

  if (!novel.id) {
    navigate({ to: '/organizations' });
    return null;
  }


  const methods = useForm<OrganizationFormValues>({
    resolver: zodResolver(OrganizationFormSchema),
    defaultValues: {
      name: '',
      type: '',
      shortDescription: '',
      visualDescription: '',
      images: [],
      primaryImageId: null,
      pendingFiles: [],
      parentOrganizationIds: [],
      childOrganizationIds: [],
      childOrganizationTitles: {},
      organizationMemberGraphRelationships: [],
      organizationTitles: [],
      organizationMembers: [],
      audit: {
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    },
  });

  // Sync images and primary index from background updates (e.g. AI generation)
  useEffect(() => {
    if (combinedData?.images && JSON.stringify(combinedData.images) !== JSON.stringify(methods.getValues('images'))) {
      methods.setValue('images', combinedData.images as OrganizationFormValues['images'], { shouldDirty: true });
    }
    if (combinedData?.primaryImageId !== undefined && combinedData.primaryImageId !== methods.getValues('primaryImageId')) {
      methods.setValue('primaryImageId', combinedData.primaryImageId, { shouldDirty: true });
    }
  }, [combinedData?.images, combinedData?.primaryImageId, methods]);

  const onSubmit = async (values: OrganizationFormValues) => {
    if (!novelId) return;

    setIsSaving(true);
    try {
      const { pendingFiles, ...organizationData } = values;
      const { defined: coreData, details } = splitBySchemaShape(OrganizationFormSchema, organizationData);
      const organizationCoreData = isOrganizationHierarchyEnabled
        ? coreData
        : stripOrganizationHierarchyFields(coreData);

      let imagesUpdatedArray = [...(coreData.images || [])];

      // 1. Process images via shared hook
      if (pendingFiles && pendingFiles.length > 0) {
        const processedResults = await processImages(
          pendingFiles,
          novelId,
          `organizations/${organizationId}`,
          novel.ownerId
        );
        imagesUpdatedArray = [...imagesUpdatedArray, ...processedResults];
      }

      const primaryImageId = coreData.primaryImageId ?? imagesUpdatedArray[0]?.id ?? null;
      const mainImage = imagesUpdatedArray.find(image => image.id === primaryImageId);
      const mainImageUrl = mainImage?.profile?.url || mainImage?.original?.url || '';
      const mainImagePath = mainImage?.profile?.path || mainImage?.original?.path || '';

      // 2. Create via Access Layer
      await createOrganizationLayer(organizationId, {
        ...organizationCoreData,
        id: organizationId,
        details,
        images: imagesUpdatedArray,
        primaryImageId,
        mainImageUrl,
        mainImagePath,
      });

      if (isOrganizationHierarchyEnabled) {
        if (!characterDocs || !organizationDocs) {
          throw new Error(t('entity_form.common.related_records_loading_error'));
        }

        await syncCharactersForOrganization({
          organizationId,
          previousCharacterIds: [],
          nextCharacterIds: getOrganizationMemberIds(organizationData),
          characters: characterDocs,
          updateEntityItem,
          saveRelationship: (relationship) => saveItem('entities', 'relationships', relationship),
          deleteRelationship: (relationshipId) => deleteItem('entities', 'relationships', relationshipId),
        });

        await syncParentOrganizationsForOrganization({
          organizationId,
          previousParentOrganizationIds: [],
          nextParentOrganizationIds: getParentOrganizationIds(organizationData),
          organizations: organizationDocs,
          updateEntityItem,
          saveRelationship: (relationship) => saveItem('entities', 'relationships', relationship),
          deleteRelationship: (relationshipId) => deleteItem('entities', 'relationships', relationshipId),
        });

        await syncChildOrganizationsForOrganization({
          organizationId,
          previousChildOrganizationIds: [],
          nextChildOrganizationIds: getChildOrganizationIds(organizationData),
          organizations: organizationDocs,
          updateEntityItem,
          saveRelationship: (relationship) => saveItem('entities', 'relationships', relationship),
          deleteRelationship: (relationshipId) => deleteItem('entities', 'relationships', relationshipId),
        });
      }

      toast({
        title: t('entity_form.organizations.create_success_title'),
        description: t('entity_form.organizations.create_success_desc', { name: values.name }),
      });

      navigate({ to: '/organizations' });
    } catch (error) {
      console.error('Create error:', error);
      toast({
        title: t('entity_form.common.save_failed_title'),
        description: error instanceof Error ? error.message : t('entity_form.common.unexpected_error'),
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const onInvalid = (errors: FieldErrors<OrganizationFormValues>) => {
    console.warn('Validation errors:', errors);
    toast({
      title: t('entity_form.common.validation_failed_title'),
      description: t('entity_form.common.validation_failed_generic_desc'),
      variant: 'destructive',
    });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit, onInvalid)}>
        <Page
          title={t('entity_form.organizations.new_title')}
          description={t('entity_form.organizations.new_desc')}
          breadcrumbs={[
            { label: t('entity_list.organizations.title'), href: '/organizations' },
            { label: t('entity_form.organizations.breadcrumb_new') }
          ]}
          layoutMode="scrolling"
          actions={
            <Button
              type="submit"
              variant="contained"
              disabled={isSaving || isLoadingHierarchyRecords}
              startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <Save sx={{ fontSize: '1rem' }} />}
              sx={{ borderRadius: 2, px: 3 }}
            >
              {t('entity_form.organizations.save')}
            </Button>
          }
        >

          <Grid container spacing={4}>
            <Grid size={resolveResponsive({ xs: 12, lg: 8 })}>
              <Stack spacing={4}>
                <BasicIdentitySection />
                {isOrganizationHierarchyEnabled && <MembershipSection organizationId={organizationId} />}
                {novelId && <CustomFieldsSection novelId={novelId} sections={organizationSettings} />}
              </Stack>
            </Grid>

            <Grid size={resolveResponsive({ xs: 12, lg: 4 })}>
              <Box component="aside" sx={{ position: { lg: 'sticky' }, top: 24 }}>
                <ImageManager
                  onUpdate={(updates) => {
                    (Object.keys(updates) as Array<keyof typeof updates>).forEach((key) => {
                      const value = updates[key];
                      if (value !== undefined) {
                        methods.setValue(key as Path<OrganizationFormValues>, value, { shouldDirty: true, shouldTouch: true });
                      }
                    });
                  }}
                  organization={{ ...methods.getValues(), id: organizationId } as DomainOrganization}
                  pendingFiles={(methods.watch('pendingFiles') as File[]) || []}
                  isProcessing={isProcessing}
                  visualDescription={methods.watch('visualDescription')}
                  onVisualDescriptionChange={(value) => methods.setValue('visualDescription', value, { shouldDirty: true })}
                />
              </Box>
            </Grid>
          </Grid>
        </Page>
        <FloatingSaveButton
          isSaving={isSaving}
          disabled={isSaving || isLoadingHierarchyRecords}
          label={t('entity_form.organizations.save')}
        />
      </form>
    </FormProvider>
  );
}

export default function NewOrganizationPage() {
  const { t } = useTranslation();
  return (
    <AuthGuard>
      <Suspense fallback={
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, gap: 3 }}>
          <CircularProgress color="primary" thickness={2} size={48} />
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.2em' }}>
            {t('entity_form.organizations.materializing')}
          </Typography>
        </Box>
      }>
        <NewOrganizationForm />
      </Suspense>
    </AuthGuard>
  );
}
